import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { ResponseArtifactDTO } from '@dreamai/types';
import api from '../../../api';
import { usePusher } from '../../../contexts/PusherContext';
import TransactionalSheet, { SheetPresentation } from './TransactionalSheet';
import { EyeIcon, EyeOffIcon, LockIcon } from './transactional-icons';
import { SiteGlyph, displayHost } from './CredentialSheetSections';
import SecureInputStage from './SecureInputStage';
import {
  PAYMENT_PRIVATE_ONLY_MESSAGE,
  paymentCredentialKind,
} from './purchaseApprovalArtifact';
import './secure-input-sheet.scss';

type SecureInputKind = 'login' | 'mfa' | 'payment';

interface SecureInputField {
  name: string;
  type: string;
}

interface SignInOption {
  id: string;
  label: string;
}

/**
 * How the site is signed in to: `provider` when this request goes through a
 * shared identity provider (the account entered is that provider's), or
 * `methods` when nothing is stored yet and the site offers several ways in —
 * the user then says which one their account uses.
 */
interface SignInFacts {
  provider: SignInOption | null;
  methods: SignInOption[] | null;
}

interface SecureInputRequest {
  id: string;
  kind: SecureInputKind;
  state: string;
  reasonCode?: string | null;
  origin: string;
  accountLabel?: string | null;
  /** The account Dream thinks the user uses here; prefilled, always editable. */
  suggestedAccount?: string | null;
  fields: SecureInputField[];
  signIn?: SignInFacts | null;
  expiresAt: string | number;
  nonce?: string | null;
}

interface PendingApproval {
  numberToMatch: string | null;
  expiresAt: string;
}

interface SecureInputStatus {
  secureRequestId: string;
  state: string;
  reasonCode?: string | null;
  waitingOn?: string | null;
  pendingApproval?: PendingApproval | null;
}

interface SecureInputRequestModalProps {
  show: boolean;
  artifact: ResponseArtifactDTO;
  secureRequestId: string;
  onClose: () => void;
  onOptimisticAction?: (
    artifact: ResponseArtifactDTO,
    action: 'submit' | 'dismiss',
  ) => void;
  readOnly?: boolean;
  /**
   * Public share token (WhatsApp `/artifact/:shareToken` page). When set the
   * form talks to the share-token-authenticated secure-input endpoints and
   * never asks Auth0 for a token.
   */
  shareToken?: string;
  /** `modal` (default) layers over the app; `standalone` is the page. */
  presentation?: SheetPresentation;
  /**
   * Whether the sheet can be dismissed without acting (close control,
   * Escape, scrim). Standalone pages are never dismissible.
   */
  dismissible?: boolean;
}

type UserChannel = {
  bind: (event: string, callback: (data: SecureInputStatus) => void) => void;
  unbind: (event: string, callback: (data: SecureInputStatus) => void) => void;
};

const GENERIC_FAILURE =
  'Couldn’t complete this securely — check with Dream in chat.';
const LINK_CLOSED_MESSAGE =
  'This link has expired — Dream has been told and will follow up in chat.';
const CANCELLED_MESSAGE =
  'This request was cancelled — Dream has been told and will follow up in chat.';
const RETRY_LIMIT_MESSAGE =
  'Three attempts didn’t go through, so this link is closed — Dream has been told and will follow up in chat.';
const FORM_RETRY_LIMIT = 3;

/** What the server said about a refused submission; never field values. */
interface SubmitRejection {
  reasonCode?: string | null;
  userMessage?: string | null;
  retryable?: boolean;
  attempt?: number;
  attemptsLeft?: number;
  nonce?: string | null;
}

const rejectionOf = (error: unknown): SubmitRejection | null => {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return null;
  }
  const response = error.response;
  if (typeof response !== 'object' || response === null) return null;
  if (!('data' in response)) return null;
  const data = response.data;
  if (typeof data !== 'object' || data === null) return null;
  return data as SubmitRejection;
};

/** Which single value a payment request is waiting on. */
type PaymentCode = 'cvv' | 'bank_code';

const paymentCodeOf = (request: SecureInputRequest): PaymentCode =>
  request.fields.some((f) => f.name === 'cvv') ? 'cvv' : 'bank_code';

/** Mirrors the server's acceptance rules so a bad code never leaves the sheet. */
const CVV_PATTERN = /^\d{3,4}$/;
const BANK_CODE_MAX = 16;
export const isValidPaymentCode = (
  kind: PaymentCode,
  value: string,
): boolean => {
  const code = value.trim();
  return kind === 'cvv'
    ? CVV_PATTERN.test(code)
    : code.length > 0 && code.length <= BANK_CODE_MAX;
};

/**
 * A payment request only ever waits for one code; once the user has handed it
 * over (or the executor is already past the code step) the sheet must not ask
 * again.
 */
const PAYMENT_CODE_HANDED_OVER = new Set([
  'submitted',
  'filling',
  'verifying',
  'paid',
]);

/**
 * A login or code request is done for the user the moment the values are
 * stored: the sign-in itself runs in the background and anything the site asks
 * for next (a code, a corrected password) arrives as a fresh request. Reopening
 * the card after submitting shows Done, never the form again — including when
 * the sign-in itself then failed: a login card only reaches `cannot_continue`
 * once its values were submitted, and the failure is explained in chat.
 */
const CREDENTIALS_HANDED_OVER = new Set([
  'submitted',
  'filling',
  'verifying',
  'verification_required',
  'authenticated',
  'submitted_unconfirmed',
  'cannot_continue',
]);

/** States after which no further status can matter to this card. */
const TERMINAL_STATES = new Set([
  'authenticated',
  'submitted_unconfirmed',
  'paid',
  'cannot_continue',
  'expired',
  'cancelled',
]);

interface SheetCopy {
  loading: string;
  /** Hero title, split so the trailing part can take the accent colour. */
  heroLead: string;
  heroAccent: string;
  siteLine: string;
  brief: string;
  label: string;
  fieldName: string;
  placeholder: string;
  secretNoun: string;
  autoComplete: string;
  inputMode?: 'numeric';
  submitting: string;
  successLead: string;
  successAccent: string;
  successBody: string;
  expiredLead: string;
  expiredAccent: string;
  expiredBody: string;
}

const copyFor = (
  request: SecureInputRequest | null,
  artifact: ResponseArtifactDTO,
  host: string,
): SheetCopy => {
  const artifactKind = paymentCredentialKind(artifact);
  const isPayment = request
    ? request.kind === 'payment'
    : artifactKind !== null;
  if (isPayment) {
    const code: PaymentCode = request
      ? paymentCodeOf(request)
      : artifactKind === 'payment_3ds'
        ? 'bank_code'
        : 'cvv';
    return {
      loading: 'Loading secure payment step…',
      heroLead: code === 'cvv' ? 'Enter your card' : 'Enter your bank',
      heroAccent: code === 'cvv' ? 'security code' : 'verification code',
      siteLine: 'Paying',
      brief:
        code === 'cvv'
          ? 'The 3 or 4 digit code on your card. It goes straight to the secure browser session — Dream never sees it.'
          : 'Your bank sent you a code to confirm this payment. It goes straight to the secure browser session — Dream never sees it.',
      label: code === 'cvv' ? 'Card security code' : 'Bank verification code',
      fieldName: code === 'cvv' ? 'cvv' : 'code',
      placeholder: 'Enter code',
      secretNoun: 'code',
      autoComplete: code === 'cvv' ? 'cc-csc' : 'one-time-code',
      inputMode: 'numeric',
      submitting: 'Sending securely…',
      successLead: 'Code sent',
      successAccent: 'securely',
      successBody:
        'Dream is completing the payment and will tell you how it went.',
      expiredLead: 'This payment step',
      expiredAccent: 'expired',
      expiredBody:
        'Dream will check the payment status and let you know what happened.',
    };
  }
  const isMfa = request?.kind === 'mfa';
  const provider = request?.signIn?.provider ?? null;
  return {
    loading: 'Loading secure sign-in…',
    heroLead: isMfa ? 'Enter your code' : 'Secure sign-in',
    heroAccent: `${isMfa ? 'for' : 'to'} ${host}`,
    siteLine:
      [
        provider ? `via ${provider.label}` : null,
        request?.suggestedAccount
          ? `Suggested account: ${request.suggestedAccount} (you can change it)`
          : request?.accountLabel,
      ]
        .filter(Boolean)
        .join(' · ') || 'Sign in',
    brief: isMfa
      ? 'Enter the verification code the site sent you. Dream uses it once, in the secure browser session.'
      : provider
        ? `Enter the ${provider.label} account you use for ${host}. Dream uses it only in the secure browser session.`
        : 'Enter your sign-in details. Dream uses them only in the secure browser session.',
    label: isMfa ? 'Verification code' : 'Password',
    fieldName: isMfa ? 'code' : 'password',
    placeholder: isMfa ? 'Enter code' : 'Enter password',
    secretNoun: isMfa ? 'code' : 'password',
    autoComplete: isMfa ? 'one-time-code' : 'current-password',
    submitting: isMfa ? 'Sending securely…' : 'Saving securely…',
    successLead: isMfa ? 'Code sent' : 'Details saved',
    successAccent: 'securely',
    successBody: isMfa
      ? 'Dream is finishing your sign-in in the background. You can close this.'
      : 'Dream is signing you in in the background. You can close this — if the site asks for anything else, like a verification code, you’ll get a new request.',
    expiredLead: 'This sign-in',
    expiredAccent: 'expired',
    expiredBody: LINK_CLOSED_MESSAGE,
  };
};

const reasonMessage = (reasonCode?: string | null): string => {
  switch (reasonCode) {
    case 'origin_not_approved':
    case 'wrong_account':
    case 'budget_exhausted':
    case 'session_changed':
    case 'expired':
    case 'kill_switch':
      return GENERIC_FAILURE;
    case 'credential_rejected':
      return 'That sign-in was rejected — check and re-enter';
    case 'retry_limit':
      return RETRY_LIMIT_MESSAGE;
    case 'cancelled':
      return CANCELLED_MESSAGE;
    default:
      return GENERIC_FAILURE;
  }
};

const statusCode = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return undefined;
  }
  const response = error.response;
  if (typeof response !== 'object' || response === null) return undefined;
  if (!('status' in response) || typeof response.status !== 'number') {
    return undefined;
  }
  return response.status;
};

const formatExpiry = (expiresAt: string | number, now: number): string => {
  const timestamp =
    typeof expiresAt === 'number' ? expiresAt : new Date(expiresAt).getTime();
  const remaining = Math.max(0, timestamp - now);
  const seconds = Math.ceil(remaining / 1000);
  if (seconds > 60) {
    const minutes = Math.ceil(seconds / 60);
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  return `${seconds} second${seconds === 1 ? '' : 's'}`;
};

const SecureInputRequestModal: React.FC<SecureInputRequestModalProps> = ({
  show,
  artifact,
  secureRequestId,
  onClose,
  onOptimisticAction,
  readOnly = false,
  shareToken,
  presentation = 'modal',
  dismissible = true,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const canDismiss = presentation === 'modal' && dismissible;
  const basePath = shareToken
    ? `/public/artifacts/${shareToken}/secure-input/${secureRequestId}`
    : `/secure-input/${secureRequestId}`;
  const pusherContext = usePusher(true);
  const userChannel = pusherContext?.userChannel as UserChannel | undefined;
  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const nonceRef = useRef<string | null>(null);
  const pollTimerRef = useRef<number | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const doneRef = useRef(false);
  const submittedHereRef = useRef(false);
  const openRef = useRef(false);
  const [request, setRequest] = useState<SecureInputRequest | null>(null);
  const [phase, setPhase] = useState<
    'loading' | 'form' | 'submitting' | 'success' | 'expired' | 'error'
  >('loading');
  const [message, setMessage] = useState<string | null>(null);
  const [pendingApproval, setPendingApproval] =
    useState<PendingApproval | null>(null);
  const [revealSecret, setRevealSecret] = useState(false);
  const [signInMethod, setSignInMethod] = useState<string | null>(null);
  const [passwordless, setPasswordless] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [paymentCodeValid, setPaymentCodeValid] = useState(false);
  const [attempt, setAttempt] = useState<number | null>(null);
  const [closedAs, setClosedAs] = useState<'expired' | 'cancelled'>('expired');
  const prefillUsernameRef = useRef<string | null>(null);
  const paymentCode: PaymentCode | null =
    request?.kind === 'payment' ? paymentCodeOf(request) : null;
  const refusePublicPayment =
    !!shareToken && paymentCredentialKind(artifact) !== null;
  const host = request ? displayHost(request.origin) || request.origin : '';
  const copy = copyFor(request, artifact, host);

  const clearInputs = useCallback(() => {
    if (usernameRef.current) usernameRef.current.value = '';
    if (passwordRef.current) passwordRef.current.value = '';
    if (codeRef.current) codeRef.current.value = '';
  }, []);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current !== null) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const authConfig = useCallback(async () => {
    if (shareToken) return {};
    const token = await getAccessTokenSilently();
    return { headers: { Authorization: `Bearer ${token}` } };
  }, [getAccessTokenSilently, shareToken]);

  const loadRequest = useCallback(
    async (
      showLoading = false,
      resetPhase = true,
    ): Promise<SecureInputRequest | null> => {
      if (showLoading) setPhase('loading');
      try {
        const response = await api.get<SecureInputRequest>(
          basePath,
          await authConfig(),
        );
        const nextRequest = response.data;
        if (nextRequest.kind === 'payment' && shareToken) {
          nonceRef.current = null;
          setRequest(null);
          setPhase('error');
          setMessage(PAYMENT_PRIVATE_ONLY_MESSAGE);
          return null;
        }
        nonceRef.current = nextRequest.nonce ?? null;
        setRequest(nextRequest);
        setMessage(
          nextRequest.reasonCode === 'credential_rejected'
            ? reasonMessage(nextRequest.reasonCode)
            : null,
        );
        if (resetPhase) {
          const expiresAt =
            typeof nextRequest.expiresAt === 'number'
              ? nextRequest.expiresAt
              : new Date(nextRequest.expiresAt).getTime();
          const handedOver =
            nextRequest.kind === 'payment'
              ? PAYMENT_CODE_HANDED_OVER.has(nextRequest.state)
              : CREDENTIALS_HANDED_OVER.has(nextRequest.state);
          if (handedOver) {
            doneRef.current = true;
            setPhase('success');
          } else if (
            expiresAt <= Date.now() ||
            nextRequest.state === 'expired' ||
            nextRequest.state === 'cancelled'
          ) {
            setClosedAs(
              nextRequest.state === 'cancelled' ? 'cancelled' : 'expired',
            );
            setPhase('expired');
          } else if (nextRequest.state === 'cannot_continue') {
            setPhase('error');
            setMessage(reasonMessage(nextRequest.reasonCode));
          } else {
            setPhase('form');
          }
        }
        return nextRequest;
      } catch (error) {
        if (statusCode(error) === 410) {
          const closed = rejectionOf(error);
          setClosedAs(
            closed?.reasonCode === 'cancelled' ? 'cancelled' : 'expired',
          );
          setPhase('expired');
          setMessage(closed?.userMessage ?? null);
        } else {
          setPhase('error');
          setMessage(GENERIC_FAILURE);
        }
        return null;
      }
    },
    [authConfig, basePath, shareToken],
  );

  /**
   * The user's part is over: show Done. A payment code closes the sheet on
   * its own; a login or code card stays until the user closes it (they may
   * park it — nothing here waits on the sign-in). Hosts treat the optimistic
   * `submit` as "close the sheet", so for a card that stays open it is sent
   * from `handleClose` instead.
   */
  const acknowledgeSuccess = useCallback(
    (autoClose: boolean) => {
      if (doneRef.current) return;
      doneRef.current = true;
      clearInputs();
      setMessage(null);
      setPhase('success');
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      if (autoClose) {
        stopPolling();
        onOptimisticAction?.(artifact, 'submit');
        closeTimerRef.current = window.setTimeout(onClose, 700);
      } else {
        submittedHereRef.current = true;
      }
    },
    [artifact, clearInputs, onClose, onOptimisticAction, stopPolling],
  );

  const handleStatus = useCallback(
    (status: SecureInputStatus) => {
      if (status.secureRequestId !== secureRequestId) return;
      if (doneRef.current) {
        // Whatever the sign-in does next (a code, a rejected password, an
        // unexpected site) reaches the user as a fresh request, not on this
        // card. Only a push approval is still shown here, while it lasts.
        if (TERMINAL_STATES.has(status.state)) {
          stopPolling();
          setPendingApproval(null);
        } else if (status.pendingApproval !== undefined) {
          setPendingApproval(status.pendingApproval);
        }
        return;
      }
      if (
        status.state === 'authenticated' ||
        status.state === 'submitted_unconfirmed' ||
        status.state === 'paid'
      ) {
        acknowledgeSuccess(status.state === 'paid');
        return;
      }
      if (
        status.state === 'cannot_continue' ||
        status.state === 'expired' ||
        status.state === 'cancelled'
      ) {
        stopPolling();
        if (status.state === 'expired' || status.reasonCode === 'expired') {
          setClosedAs('expired');
          setPhase('expired');
          setMessage(null);
        } else if (status.state === 'cancelled') {
          setClosedAs('cancelled');
          setPhase('expired');
          setMessage(null);
        } else {
          setPhase('error');
          setMessage(reasonMessage(status.reasonCode));
        }
      }
    },
    [acknowledgeSuccess, secureRequestId, stopPolling],
  );

  const pollStatus = useCallback(async () => {
    try {
      const response = await api.get<SecureInputStatus>(
        `${basePath}/status`,
        await authConfig(),
      );
      setPendingApproval(response.data.pendingApproval ?? null);
      handleStatus(response.data);
    } catch (error) {
      if (statusCode(error) === 410) {
        stopPolling();
        setPendingApproval(null);
        if (doneRef.current) return;
        setPhase('expired');
        setMessage(null);
      }
    }
  }, [authConfig, basePath, handleStatus, stopPolling]);

  const startPolling = useCallback(() => {
    stopPolling();
    pollTimerRef.current = window.setInterval(pollStatus, 2000);
  }, [pollStatus, stopPolling]);
  // Read through a ref by the load effect: `startPolling` changes identity
  // with the host's callbacks, and the effect must not reload (and reset the
  // form) whenever the host re-renders.
  const startPollingRef = useRef(startPolling);
  startPollingRef.current = startPolling;

  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    openRef.current = true;
    doneRef.current = false;
    submittedHereRef.current = false;
    clearInputs();
    setRequest(null);
    setMessage(null);
    setPendingApproval(null);
    setSignInMethod(null);
    setPasswordless(false);
    setRevealSecret(false);
    setNow(Date.now());
    if (refusePublicPayment) {
      setPhase('error');
      setMessage(PAYMENT_PRIVATE_ONLY_MESSAGE);
    } else {
      void loadRequest(true).then((loaded) => {
        if (cancelled) {
          stopPolling();
          return;
        }
        // Reopened after handing the details over: the sign-in may still
        // raise a push approval, which only the status endpoint carries.
        if (
          loaded &&
          loaded.kind !== 'payment' &&
          CREDENTIALS_HANDED_OVER.has(loaded.state) &&
          !TERMINAL_STATES.has(loaded.state)
        ) {
          startPollingRef.current();
        }
      });
    }
    return () => {
      cancelled = true;
      openRef.current = false;
      stopPolling();
      clearInputs();
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, [
    clearInputs,
    loadRequest,
    refusePublicPayment,
    secureRequestId,
    show,
    stopPolling,
  ]);

  useEffect(() => {
    if (!show || !request) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [request, show]);

  useEffect(() => {
    if (!show || !userChannel) return undefined;
    userChannel.bind('secure-input-status', handleStatus);
    return () => userChannel.unbind('secure-input-status', handleStatus);
  }, [handleStatus, show, userChannel]);

  // The username field is uncontrolled and unmounted while submitting, so a
  // suggestion (or what the user typed before a refused attempt) is placed
  // when the form is on screen; the user can change it.
  useEffect(() => {
    if (phase !== 'form' || !usernameRef.current) return;
    const value = prefillUsernameRef.current ?? request?.suggestedAccount;
    prefillUsernameRef.current = null;
    if (value && !usernameRef.current.value) usernameRef.current.value = value;
  }, [phase, request]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!request || phase === 'submitting' || readOnly) return;

    let username = usernameRef.current?.value ?? '';
    let password = passwordRef.current?.value ?? '';
    let code = codeRef.current?.value ?? '';
    if (paymentCode && !isValidPaymentCode(paymentCode, code)) {
      code = '';
      setMessage(
        paymentCode === 'cvv'
          ? 'Enter the 3 or 4 digit code from your card.'
          : 'Enter the code your bank sent you.',
      );
      return;
    }
    code = code.trim();
    clearInputs();
    setPaymentCodeValid(false);
    setPhase('submitting');
    setMessage(null);
    setPendingApproval(null);

    try {
      if (!nonceRef.current) {
        setPhase('error');
        setMessage(GENERIC_FAILURE);
        return;
      }
      // A code-only site is declared explicitly; the password never travels
      // with it, so the server can tell "no password" from "forgot it".
      const login =
        offerPasswordless && passwordless
          ? { username, passwordless: true }
          : { username, password };
      const fields =
        request.kind === 'payment'
          ? { [copy.fieldName]: code }
          : request.kind === 'mfa'
            ? { code }
            : signInMethods
              ? { ...login, signInMethod }
              : login;
      let retried = false;
      let submitted = false;
      while (!submitted) {
        try {
          await api.post(
            `${basePath}/submit`,
            { nonce: nonceRef.current, fields },
            await authConfig(),
          );
          // Stored: the user is done. The outcome comes back through the
          // agent (and, if the site wants more, a fresh request), not this
          // sheet; polling only keeps a push-approval notice visible. A
          // status that overtook this response (or a close) already settled
          // the card, so no polling then.
          const settled = doneRef.current || !openRef.current;
          acknowledgeSuccess(request.kind === 'payment');
          if (request.kind !== 'payment' && !settled) startPolling();
          submitted = true;
        } catch (error) {
          if (statusCode(error) !== 409 || retried) throw error;
          retried = true;
          const freshRequest = await loadRequest(false, false);
          if (!freshRequest?.nonce) throw error;
          nonceRef.current = freshRequest.nonce;
        }
      }
    } catch (error) {
      const rejection = rejectionOf(error);
      if (statusCode(error) === 410) {
        setClosedAs(
          rejection?.reasonCode === 'cancelled' ? 'cancelled' : 'expired',
        );
        setPhase('expired');
        setMessage(rejection?.userMessage ?? null);
      } else if (rejection?.retryable && request.kind !== 'payment') {
        // The form refused this attempt for a reason the user can fix: say
        // why, keep the fields, and let them try again.
        if (rejection.nonce) nonceRef.current = rejection.nonce;
        prefillUsernameRef.current = username;
        setAttempt(rejection.attempt ?? null);
        setMessage(rejection.userMessage ?? GENERIC_FAILURE);
        setPhase('form');
      } else {
        setPhase('error');
        setMessage(rejection?.userMessage ?? GENERIC_FAILURE);
      }
    } finally {
      username = '';
      password = '';
      code = '';
    }
  };

  const handleClose = useCallback(() => {
    openRef.current = false;
    clearInputs();
    nonceRef.current = null;
    stopPolling();
    if (submittedHereRef.current) {
      submittedHereRef.current = false;
      onOptimisticAction?.(artifact, 'submit');
    }
    onClose();
  }, [artifact, clearInputs, onClose, onOptimisticAction, stopPolling]);

  const handleCancel = async () => {
    clearInputs();
    nonceRef.current = null;
    stopPolling();
    try {
      await api.post(`${basePath}/cancel`, {}, await authConfig());
      onOptimisticAction?.(artifact, 'dismiss');
    } finally {
      handleClose();
    }
  };

  const singleField = request?.kind === 'mfa' || request?.kind === 'payment';
  const signInMethods =
    !singleField &&
    request?.signIn?.methods &&
    request.signIn.methods.length > 1
      ? request.signIn.methods
      : null;
  const needsSignInMethod = signInMethods !== null && signInMethod === null;
  // Only the destination site itself can be code-only; a shared identity
  // provider (Google, Microsoft, ...) always takes its own password.
  const providerSignIn =
    request?.signIn?.provider != null ||
    (signInMethod !== null && signInMethod !== 'password');
  const offerPasswordless = request?.kind === 'login' && !providerSignIn;
  const passwordDisabled = readOnly || (offerPasswordless && passwordless);

  const togglePasswordless = (checked: boolean) => {
    setPasswordless(checked);
    if (checked && passwordRef.current) passwordRef.current.value = '';
  };
  const selectSignInMethod = (method: string) => {
    setSignInMethod(method);
    if (method !== 'password') setPasswordless(false);
  };
  const expired =
    request &&
    (typeof request.expiresAt === 'number'
      ? request.expiresAt <= now
      : new Date(request.expiresAt).getTime() <= now);

  return (
    <TransactionalSheet
      show={show}
      presentation={presentation}
      onClose={canDismiss ? handleClose : undefined}
      leading={phase === 'form' ? <LockIcon /> : undefined}
      className='secure-input-sheet'
      ariaLabelledBy='secure-input-title'
      holdOpen={phase === 'submitting'}
      footer={
        phase === 'form' ? (
          <>
            <button
              type='submit'
              className='tx-btn-primary'
              form='secure-input-form'
              disabled={
                readOnly ||
                expired === true ||
                needsSignInMethod ||
                (paymentCode !== null && !paymentCodeValid)
              }
            >
              {attempt ? 'Try again' : 'Continue'}
            </button>
            <button
              type='button'
              className='tx-btn-text'
              onClick={handleCancel}
              disabled={readOnly}
            >
              Cancel
            </button>
          </>
        ) : undefined
      }
    >
      {phase === 'loading' && <p className='brief'>{copy.loading}</p>}
      {phase === 'expired' && (
        <SecureInputStage
          key='expired'
          kind='expired'
          title={
            <>
              {copy.expiredLead}{' '}
              <span className='accent'>
                {closedAs === 'cancelled' && request?.kind !== 'payment'
                  ? 'was cancelled'
                  : copy.expiredAccent}
              </span>
            </>
          }
          titleId='secure-input-title'
        >
          <p>
            {message ||
              (closedAs === 'cancelled' && request?.kind !== 'payment'
                ? CANCELLED_MESSAGE
                : copy.expiredBody)}
          </p>
          <button
            type='button'
            className='tx-btn-primary'
            onClick={handleClose}
          >
            Close
          </button>
        </SecureInputStage>
      )}
      {phase === 'success' && !pendingApproval && (
        <SecureInputStage
          key='signed-in'
          kind='signed-in'
          title={
            <>
              {copy.successLead}{' '}
              <span className='accent'>{copy.successAccent}</span>
            </>
          }
          titleId='secure-input-title'
        >
          <p>{copy.successBody}</p>
          {request?.kind !== 'payment' && (
            <button
              type='button'
              className='tx-btn-primary'
              onClick={handleClose}
            >
              Done
            </button>
          )}
        </SecureInputStage>
      )}
      {phase === 'success' && pendingApproval && (
        <SecureInputStage
          key='approve'
          kind='approve'
          title={
            <>
              Approve the sign-in <span className='accent'>on your phone</span>
            </>
          }
          titleId='secure-input-title'
        >
          <p>
            Your details are saved. {host || 'The site'} has sent a sign-in
            approval to your phone or authenticator app — approve it there
            within {formatExpiry(pendingApproval.expiresAt, now)} and Dream will
            carry on.
          </p>
          {pendingApproval.numberToMatch && (
            <div className='number-match pane'>
              <span className='number-match-label'>
                If you’re asked for a number, choose
              </span>
              <span className='number-match-value'>
                {pendingApproval.numberToMatch}
              </span>
            </div>
          )}
          <button
            type='button'
            className='tx-btn-primary'
            onClick={handleClose}
          >
            Done
          </button>
        </SecureInputStage>
      )}
      {phase === 'error' && (
        <SecureInputStage
          key='error'
          kind='error'
          title='Unable to continue'
          titleId='secure-input-title'
        >
          <p>{message || GENERIC_FAILURE}</p>
          <button
            type='button'
            className='tx-btn-primary'
            onClick={handleClose}
          >
            Close
          </button>
        </SecureInputStage>
      )}
      {phase === 'submitting' && (
        <SecureInputStage
          key='signing-in'
          kind='signing-in'
          title={copy.submitting}
          titleId='secure-input-title'
          busy
        />
      )}
      {phase === 'form' && request && (
        <>
          <div className='tx-head'>
            <h2 className='tx-title hero' id='secure-input-title'>
              {copy.heroLead} <span className='accent'>{copy.heroAccent}</span>
            </h2>
            <div className='about'>
              <div className='site'>
                <SiteGlyph site={request.origin} />
                <span>{host}</span>
              </div>
              <span>{copy.siteLine}</span>
            </div>
            {message ? (
              <p className='brief error' role='alert'>
                {message}
              </p>
            ) : (
              <p className='brief'>{copy.brief}</p>
            )}
            <p className='fine' role='timer'>
              Expires in {formatExpiry(request.expiresAt, now)}
              {attempt
                ? ` · Attempt ${Math.min(attempt + 1, FORM_RETRY_LIMIT)} of ${FORM_RETRY_LIMIT}`
                : ''}
            </p>
          </div>
          <form
            id='secure-input-form'
            className='fields pane'
            onSubmit={handleSubmit}
          >
            {signInMethods && (
              <fieldset className='field'>
                <legend>How do you sign in here?</legend>
                {signInMethods.map((method) => (
                  <label
                    key={method.id}
                    className='choice'
                    htmlFor={`secure-input-method-${method.id}`}
                  >
                    <input
                      id={`secure-input-method-${method.id}`}
                      type='radio'
                      name='signInMethod'
                      value={method.id}
                      checked={signInMethod === method.id}
                      onChange={() => selectSignInMethod(method.id)}
                      disabled={readOnly}
                    />
                    {method.label}
                  </label>
                ))}
              </fieldset>
            )}
            {!singleField && (
              <div className='field'>
                <label htmlFor='secure-input-username'>Username</label>
                <div className='input'>
                  <input
                    ref={usernameRef}
                    id='secure-input-username'
                    name='username'
                    type='text'
                    autoCapitalize='none'
                    autoComplete='username'
                    placeholder='email@example.com'
                    data-sensitive='true'
                    data-sentry-mask='true'
                    className='ph-no-capture'
                    disabled={readOnly}
                  />
                </div>
              </div>
            )}
            <div className='field'>
              <label htmlFor='secure-input-secret'>{copy.label}</label>
              <div
                className={
                  passwordDisabled && !readOnly
                    ? 'input input--reveal input-off'
                    : 'input input--reveal'
                }
              >
                <input
                  ref={singleField ? codeRef : passwordRef}
                  id='secure-input-secret'
                  name={copy.fieldName}
                  type={revealSecret ? 'text' : 'password'}
                  inputMode={copy.inputMode}
                  autoCapitalize='none'
                  autoComplete={copy.autoComplete}
                  placeholder={copy.placeholder}
                  required={paymentCode !== null}
                  maxLength={
                    paymentCode === 'cvv'
                      ? 4
                      : paymentCode === 'bank_code'
                        ? BANK_CODE_MAX
                        : undefined
                  }
                  onChange={
                    paymentCode
                      ? (event) => {
                          setPaymentCodeValid(
                            isValidPaymentCode(paymentCode, event.target.value),
                          );
                          if (message) setMessage(null);
                        }
                      : undefined
                  }
                  data-sensitive='true'
                  data-sentry-mask='true'
                  className='ph-no-capture'
                  disabled={passwordDisabled}
                  aria-disabled={passwordDisabled}
                />
                <button
                  type='button'
                  className='reveal'
                  aria-label={
                    revealSecret
                      ? `Hide ${copy.secretNoun}`
                      : `Show ${copy.secretNoun}`
                  }
                  aria-pressed={revealSecret}
                  onClick={() => setRevealSecret((value) => !value)}
                  disabled={passwordDisabled}
                >
                  {revealSecret ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>
            {offerPasswordless && (
              <label
                className='choice passwordless'
                htmlFor='secure-input-passwordless'
              >
                <input
                  id='secure-input-passwordless'
                  type='checkbox'
                  name='passwordless'
                  checked={passwordless}
                  onChange={(event) => togglePasswordless(event.target.checked)}
                  disabled={readOnly}
                />
                This site doesn’t use a password — it sends me a code
              </label>
            )}
          </form>
        </>
      )}
    </TransactionalSheet>
  );
};

export default SecureInputRequestModal;
